import {
  Link,
  Outlet,
  createRootRouteWithContext,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Button } from '@/components/ui/button';
import { CommandPaletteHost } from '@/features/command-palette';
import { Toaster } from '@/components/ui/sonner';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { SettingsLinkRedirect } from '@/features/notifications';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
});

export function RootComponent() {
  // The client comes from the router context, not from the module singleton.
  // main.tsx supplies the singleton in production; tests supply their own, which
  // is what keeps one test's cache from answering another test's query.
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      {/*
        Above the shell, deliberately (spec 077). `ShellFrame` serves the workspace and
        the console, but the workspace *list* has no shell at all — it is the front door
        and renders its own header — so mounting there would leave the palette
        unavailable on the one page whose whole purpose is choosing a workspace.
      */}
      <CommandPaletteHost />
      {/* Above the shell for the same reason: the account menu on the
          workspace list opens it too (spec 133). */}
      <SettingsLinkRedirect />
      <Toaster />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export function RootErrorComponent({ error }: { error: unknown }) {
  // The exception goes to the console, not onto the page. This is the last
  // surface before a blank screen, so it is the one most likely to be seen by
  // someone who cannot act on a stack trace; every other boundary in the app
  // already shows a sentence and a way on. The detail is still one keypress
  // away for anyone who wants it.
  useEffect(() => {
    console.error('Unhandled error at the application root', error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-10 text-center">
      <h2 className="text-xl font-semibold">Something went wrong</h2>
      <p className="text-muted-foreground max-w-prose text-sm">
        The application could not finish loading this page. Reloading usually
        clears it. If it keeps happening, the details are in the browser
        console.
      </p>
      <Button onClick={() => window.location.reload()}>Try again</Button>
    </div>
  );
}

export function RootNotFoundComponent() {
  // Uses the shared surface so every not-found case in the application reads the
  // same and exposes the same heading level.
  return (
    <div className="flex min-h-screen flex-col">
      <NotFoundState
        title="Page not found"
        description="This address does not match anything in the application. It may have been mistyped, or the page may have moved."
        action={
          <Link
            to="/"
            className="bg-primary text-primary-foreground focus-visible:ring-ring inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 focus-visible:ring-3 focus-visible:outline-none"
          >
            Back to all workspaces
          </Link>
        }
      />
    </div>
  );
}
